<script setup lang="ts">
import { gsap } from "gsap";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const POPUP_WIDTH = 392;
const HIGHLIGHT_FADE_IN = 0.2;
const HIGHLIGHT_HOLD = 0.5;
const HIGHLIGHT_FADE_OUT = 0.2;
const HIGHLIGHT_CLICK_OFFSET = HIGHLIGHT_FADE_IN + 0.2;
type PreviewDemoWindow = Window & {
  __TUCUI_PREVIEW_READY__?: boolean;
  __TUCUI_PREVIEW_START_DETECTION__?: () => void;
  __TUCUI_PREVIEW_FINISH_DETECTION__?: () => void;
  __TUCUI_PREVIEW_RESET__?: (cycleId?: number) => void;
};
const stage = ref<HTMLElement | null>(null);
const viewport = ref<HTMLElement | null>(null);
const iframe = ref<HTMLIFrameElement | null>(null);
const cursor = ref<HTMLElement | null>(null);
const cursorIcon = ref<SVGElement | null>(null);
const cursorRing = ref<HTMLElement | null>(null);
const focusHalo = ref<HTMLElement | null>(null);
const pluginView = ref<HTMLElement | null>(null);
const archivePanel = ref<HTMLElement | null>(null);
const archiveTree = ref<HTMLElement | null>(null);
const archiveOpen = ref(false);
const activeView = ref<"plugin" | "archive">("plugin");
const scale = ref(1);
const contentHeight = ref(1);
const heightReady = ref(false);
const reducedMotion = ref(false);
const mobileDemo = ref(false);
const cycleCount = ref(0);
const retryId = ref(0);
const loadFailed = ref(false);
const frameReady = ref(false);
let currentTargetSelector = "";
let currentHaloSelector = "";
let timeline: ReturnType<typeof gsap.timeline> | null = null;
let gsapContext: ReturnType<typeof gsap.context> | null = null;
let media: ReturnType<typeof gsap.matchMedia> | null = null;
let resizeObserver: ResizeObserver | null = null;
let frameResizeObserver: ResizeObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;
let readyTimeout: ReturnType<typeof gsap.delayedCall> | null = null;
let readyFrameId = 0;
let readyDeadline = 0;
let cycleMeasureFrameId = 0;
let heightTween: ReturnType<typeof gsap.to> | null = null;
let cycleRestartCall: ReturnType<typeof gsap.delayedCall> | null = null;
let hasValidHeight = false;

const iframeSource = computed(() => {
  const mode = reducedMotion.value ? "final" : "scripted";
  return `/demo.html?preview=1&demo=${mode}&typography=standard&retry=${retryId.value}`;
});
const viewportStyle = computed(() => ({ height: `${contentHeight.value * scale.value}px` }));
const canvasStyle = computed(() => ({
  height: `${contentHeight.value}px`,
  transform: `scale(${scale.value})`,
}));
const iframeStyle = computed(() => ({ height: `${contentHeight.value}px` }));

function demoDocument(): Document | null {
  try {
    return iframe.value?.contentDocument ?? null;
  } catch {
    return null;
  }
}

function target(selector: string): HTMLElement | null {
  return stage.value?.querySelector<HTMLElement>(selector)
    ?? demoDocument()?.querySelector<HTMLElement>(selector)
    ?? null;
}

function targetRatio(selector: string): { x: number; y: number } {
  const ratios: Record<string, { x: number; y: number }> = {
    '[data-media-group="sku"]': { x: 0.5, y: 0.54 },
    "#libraryBackButton": { x: 0.5, y: 0.5 },
    "#saveButton": { x: 0.14, y: 0.5 },
    "#overviewDownloadAlert": { x: 0.9, y: 0.5 },
    ".scripted-demo__archive-button": { x: 0.92, y: 0.5 },
  };
  return ratios[selector] ?? { x: 0.5, y: 0.5 };
}

function pointFor(selector: string): { x: number; y: number } | null {
  const element = target(selector);
  const rect = element?.getBoundingClientRect();
  if (!rect || rect.width <= 1 || rect.height <= 1) return null;
  const ratio = targetRatio(selector);
  if (element?.ownerDocument === document && stage.value) {
    const stageRect = stage.value.getBoundingClientRect();
    return {
      x: rect.left - stageRect.left + rect.width * ratio.x,
      y: rect.top - stageRect.top + rect.height * ratio.y,
    };
  }
  return {
    x: (rect.left + rect.width * ratio.x) * scale.value,
    y: (rect.top + rect.height * ratio.y) * scale.value,
  };
}

function updateScale(): void {
  const available = stage.value?.clientWidth ?? POPUP_WIDTH;
  const nextScale = Math.min(available / POPUP_WIDTH, 1);
  if (Math.abs(nextScale - scale.value) > 0.0001) {
    if (frameReady.value && activeView.value === "plugin") pinStageHeight();
    scale.value = nextScale;
    void nextTick(() => {
      if (activeView.value === "plugin" && pluginView.value) {
        transitionStageToElement(pluginView.value, !reducedMotion.value && frameReady.value);
      }
    });
  }
  if (currentTargetSelector && cursor.value) {
    const point = pointFor(currentTargetSelector);
    if (point) gsap.set(cursor.value, { x: point.x, y: point.y });
  }
  if (currentHaloSelector) placeHalo(currentHaloSelector);
}

function placeHalo(selector: string): void {
  const element = target(selector);
  if (!element || !focusHalo.value) return;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 1 || rect.height <= 1) return;
  const paddingX = 4;
  const paddingY = 3;
  const radius = Number.parseFloat(getComputedStyle(element).borderRadius) || 7;
  if (element.ownerDocument === document && stage.value) {
    const stageRect = stage.value.getBoundingClientRect();
    gsap.set(focusHalo.value, {
      x: rect.left - stageRect.left - paddingX,
      y: rect.top - stageRect.top - paddingY,
      width: rect.width + paddingX * 2,
      height: rect.height + paddingY * 2,
      borderRadius: radius + 3,
    });
  } else {
    gsap.set(focusHalo.value, {
      x: rect.left * scale.value - paddingX,
      y: rect.top * scale.value - paddingY,
      width: rect.width * scale.value + paddingX * 2,
      height: rect.height * scale.value + paddingY * 2,
      borderRadius: radius * scale.value + 3,
    });
  }
}

function updateContentHeight(nextHeight: number, animate = true): void {
  if (!Number.isFinite(nextHeight) || nextHeight <= 1) return;
  const bounded = Math.round(Math.min(600, nextHeight));
  const firstValidHeight = !hasValidHeight;
  if (bounded === contentHeight.value && !firstValidHeight) return;
  const shouldAnimate = animate && frameReady.value && activeView.value === "plugin" && !reducedMotion.value;
  if (shouldAnimate) pinStageHeight();
  contentHeight.value = bounded;
  heightReady.value = true;
  hasValidHeight = true;
  void nextTick(() => {
    if (activeView.value !== "plugin" || !pluginView.value) return;
    transitionStageToElement(pluginView.value, shouldAnimate && !firstValidHeight);
  });
}

function stageBorderBlockSize(): number {
  if (!stage.value) return 0;
  const styles = getComputedStyle(stage.value);
  return (Number.parseFloat(styles.borderTopWidth) || 0) + (Number.parseFloat(styles.borderBottomWidth) || 0);
}

function pinStageHeight(): void {
  if (!stage.value) return;
  const current = stage.value.getBoundingClientRect().height;
  heightTween?.kill();
  heightTween = null;
  gsap.set(stage.value, { height: current });
}

function transitionStageToElement(element: HTMLElement, animate = true): void {
  if (!stage.value) return;
  const targetHeight = element.getBoundingClientRect().height + stageBorderBlockSize();
  if (!Number.isFinite(targetHeight) || targetHeight <= 1) return;
  heightTween?.kill();
  heightTween = null;
  if (!animate || reducedMotion.value) {
    gsap.set(stage.value, { clearProps: "height" });
    return;
  }
  const currentHeight = stage.value.getBoundingClientRect().height;
  gsap.set(stage.value, { height: currentHeight });
  heightTween = gsap.to(stage.value, {
    height: targetHeight,
    duration: 0.34,
    ease: "power2.out",
    overwrite: "auto",
    onComplete: () => {
      heightTween = null;
      if (stage.value) gsap.set(stage.value, { clearProps: "height" });
    },
  });
}

function measureFrameContent(): number | null {
  const shell = demoDocument()?.querySelector<HTMLElement>(".app-shell");
  if (!shell) return null;
  const height = shell.getBoundingClientRect().height;
  return Number.isFinite(height) && height > 1 ? height : null;
}

function observeFrameContent(): void {
  frameResizeObserver?.disconnect();
  const shell = demoDocument()?.querySelector<HTMLElement>(".app-shell");
  if (!shell) return;
  frameResizeObserver = new ResizeObserver(() => {
    const measured = measureFrameContent();
    if (measured) updateContentHeight(measured);
  });
  frameResizeObserver.observe(shell);
}

function handlePreviewMessage(event: MessageEvent): void {
  if (event.source !== iframe.value?.contentWindow) return;
  if (event.data?.view !== "plugin" || event.data?.cycleId !== cycleCount.value) return;
  if (activeView.value !== "plugin" && event.data?.type === "popup-preview-height") return;
  if (event.data?.type === "popup-preview-loading") return;
  if (event.data?.type === "popup-preview-ready" && Number.isFinite(event.data.height)) {
    const measured = measureFrameContent();
    const candidate = measured ?? Number(event.data.height);
    updateContentHeight(candidate, false);
    if (!heightReady.value) return;
    completeFrameReady();
    return;
  }
  if (event.data?.type === "popup-preview-height" && Number.isFinite(event.data.height)) {
    const measured = measureFrameContent();
    const candidate = measured ?? Number(event.data.height);
    updateContentHeight(candidate, frameReady.value);
  }
}

function completeFrameReady(): void {
  if (!heightReady.value) return;
  readyTimeout?.kill();
  loadFailed.value = false;
  if (frameReady.value) return;
  frameReady.value = true;
  if (reducedMotion.value) {
    showReducedMotionState();
    return;
  }
  void nextTick(() => {
    buildTimeline();
    void nextTick(startDemo);
  });
}

function showReducedMotionState(): void {
  if (!frameReady.value || !heightReady.value) return;
  timeline?.kill();
  archiveOpen.value = true;
  activeView.value = "archive";
  if (pluginView.value) gsap.set(pluginView.value, { display: "none" });
  if (archivePanel.value) gsap.set(archivePanel.value, { display: "block", autoAlpha: 1, y: 0 });
  if (archiveTree.value) gsap.set(archiveTree.value, { display: "block" });
  if (stage.value) {
    gsap.set(stage.value, { clearProps: "height" });
    gsap.set(stage.value.querySelectorAll(".scripted-demo__archive-row"), { autoAlpha: 1, y: 0 });
  }
}

function pollFrameReady(): void {
  cancelAnimationFrame(readyFrameId);
  const measured = measureFrameContent();
  if (measured) updateContentHeight(measured, false);
  if (previewWindow()?.__TUCUI_PREVIEW_READY__ && measured) {
    completeFrameReady();
    return;
  }
  if (performance.now() < readyDeadline && !loadFailed.value) {
    readyFrameId = requestAnimationFrame(pollFrameReady);
  }
}

function clickElement(selector: string): void {
  const element = target(selector);
  if (!element) return;
  element.click();
}

function showArchiveView(): void {
  if (!stage.value || !pluginView.value || !archivePanel.value) return;
  pinStageHeight();
  gsap.set(pluginView.value, { display: "none" });
  gsap.set(archivePanel.value, { display: "block" });
  activeView.value = "archive";
  void nextTick(() => {
    if (archivePanel.value) transitionStageToElement(archivePanel.value);
  });
}

function revealArchiveTree(): void {
  if (!stage.value || !archiveTree.value) return;
  pinStageHeight();
  archiveOpen.value = true;
  gsap.set(archiveTree.value, { display: "block" });
  void nextTick(() => {
    if (archivePanel.value) transitionStageToElement(archivePanel.value);
  });
}

function previewWindow(): PreviewDemoWindow | null {
  return iframe.value?.contentWindow as PreviewDemoWindow | null;
}

function addCursorMove(
  sequence: ReturnType<typeof gsap.timeline>,
  selector: string,
  position: string,
): void {
  if (!cursor.value || mobileDemo.value) return;
  let segment = {
    departure: { x: 0, y: 0 },
    approach: { x: 0, y: 0 },
    destination: { x: 0, y: 0 },
    duration: 0.5,
  };
  sequence.call(() => {
    const destination = pointFor(selector);
    const currentX = Number(gsap.getProperty(cursor.value!, "x"));
    const currentY = Number(gsap.getProperty(cursor.value!, "y"));
    const from = {
      x: Number.isFinite(currentX) ? currentX : 348 * scale.value,
      y: Number.isFinite(currentY) ? currentY : 44 * scale.value,
    };
    if (!destination) {
      currentTargetSelector = "";
      segment = { departure: from, approach: from, destination: from, duration: 0 };
      gsap.set(cursor.value!, { autoAlpha: 0 });
      return;
    }
    currentTargetSelector = selector;
    const dx = destination.x - from.x;
    const dy = destination.y - from.y;
    const distance = Math.hypot(dx, dy);
    const directionLength = Math.max(distance, 1);
    const normal = { x: -dy / directionLength, y: dx / directionLength };
    const direction = { x: dx / directionLength, y: dy / directionLength };
    const bend = Math.min(14, distance * 0.055) * (dx >= 0 ? 1 : -1);
    segment = {
      departure: {
        x: from.x + dx * 0.48 + normal.x * bend,
        y: from.y + dy * 0.48 + normal.y * bend,
      },
      approach: {
        x: destination.x - direction.x * 5,
        y: destination.y - direction.y * 5,
      },
      destination,
      duration: gsap.utils.clamp(0.32, 0.92, 0.28 + distance / 650),
    };
  }, [], position);
  sequence.to(cursor.value, {
    x: () => segment.departure.x,
    y: () => segment.departure.y,
    autoAlpha: 1,
    duration: () => segment.duration * 0.42,
    ease: "power2.out",
    overwrite: "auto",
  }, position);
  sequence.to(cursor.value, {
    x: () => segment.approach.x,
    y: () => segment.approach.y,
    autoAlpha: 1,
    duration: () => segment.duration * 0.43,
    ease: "power2.inOut",
    overwrite: "auto",
  }, ">");
  sequence.to(cursor.value, {
    x: () => segment.destination.x,
    y: () => segment.destination.y,
    duration: () => segment.duration * 0.15,
    ease: "power3.out",
    overwrite: "auto",
  }, ">");
}

function hideCursor(sequence: ReturnType<typeof gsap.timeline>, position: string): void {
  if (!cursor.value || mobileDemo.value) return;
  sequence.to(cursor.value, { autoAlpha: 0, duration: 0.16, ease: "power1.out" }, position);
  sequence.call(() => { currentTargetSelector = ""; }, [], ">");
}

function addClickAtCurrent(
  sequence: ReturnType<typeof gsap.timeline>,
  selector: string,
  position: string,
): void {
  if (!mobileDemo.value && cursorIcon.value) {
    sequence.to(cursorIcon.value, { scale: 0.91, duration: 0.09, ease: "power1.out" }, position);
    if (cursorRing.value) {
      sequence.fromTo(cursorRing.value,
        { scale: 0.22, autoAlpha: 0.58 },
        { scale: 1.12, autoAlpha: 0, duration: 0.34, ease: "power2.out", immediateRender: false },
        "<",
      );
    }
    sequence.call(() => clickElement(selector), [], "<+=0.08");
    sequence.to(cursorIcon.value, { scale: 1, duration: 0.1, ease: "power1.out" }, ">");
  } else {
    sequence.call(() => clickElement(selector), [], ">+=0.12");
  }
}

function addHighlightCue(
  sequence: ReturnType<typeof gsap.timeline>,
  selector: string,
  position: string,
): void {
  if (!focusHalo.value) return;
  sequence.call(() => {
    currentHaloSelector = selector;
    placeHalo(selector);
  }, [], position);
  sequence.to(focusHalo.value, { autoAlpha: 1, duration: HIGHLIGHT_FADE_IN, ease: "power2.out" }, position);
  sequence.to(focusHalo.value, { autoAlpha: 1, duration: HIGHLIGHT_HOLD }, ">");
  sequence.to(focusHalo.value, {
    autoAlpha: 0,
    duration: HIGHLIGHT_FADE_OUT,
    ease: "power2.out",
    onComplete: () => { currentHaloSelector = ""; },
  }, ">");
}

function addHighlightedClick(
  sequence: ReturnType<typeof gsap.timeline>,
  selector: string,
  position: string,
): void {
  addCursorMove(sequence, selector, position);
  const cuePosition = `${position}-highlight`;
  sequence.addLabel(cuePosition, ">");
  addHighlightCue(sequence, selector, cuePosition);
  addClickAtCurrent(sequence, selector, `${cuePosition}+=${HIGHLIGHT_CLICK_OFFSET}`);
}

function addDisappearingTargetClick(
  sequence: ReturnType<typeof gsap.timeline>,
  selector: string,
  position: string,
): void {
  addCursorMove(sequence, selector, position);
  const cuePosition = `${position}-highlight`;
  sequence.addLabel(cuePosition, ">");
  addHighlightCue(sequence, selector, cuePosition);
  if (focusHalo.value) sequence.set(focusHalo.value, { autoAlpha: 0 }, ">");
  sequence.call(() => { currentHaloSelector = ""; }, [], "<");
  addClickAtCurrent(sequence, selector, ">");
}

function buildTimeline(): void {
  timeline?.kill();
  if (!frameReady.value || reducedMotion.value || !stage.value) return;
  const sequence = gsap.timeline({
    paused: true,
    defaults: { ease: "power3.out" },
    onComplete: scheduleCycleRestart,
  });
  timeline = sequence;
  if (cursor.value) gsap.set(cursor.value, { autoAlpha: mobileDemo.value ? 0 : 0, x: 348 * scale.value, y: 44 * scale.value });
  archiveOpen.value = false;
  if (archivePanel.value) gsap.set(archivePanel.value, { display: "none", autoAlpha: 0 });
  if (archiveTree.value) gsap.set(archiveTree.value, { display: "none" });
  if (pluginView.value) gsap.set(pluginView.value, { display: "block", autoAlpha: 1, y: 0 });
  activeView.value = "plugin";

  sequence.addLabel("detect-start", 0);
  sequence.addLabel("detect-start-highlight", "detect-start");
  addHighlightCue(sequence, "#detectionState", "detect-start-highlight");
  sequence.to({}, { duration: 1.35 }, ">");
  sequence.call(() => previewWindow()?.__TUCUI_PREVIEW_START_DETECTION__?.(), [], ">");
  sequence.addLabel("detecting", ">");
  sequence.to({}, { duration: 1.1 }, "detecting");
  sequence.call(() => previewWindow()?.__TUCUI_PREVIEW_FINISH_DETECTION__?.(), [], ">");
  sequence.addLabel("detected", ">");
  sequence.to({}, { duration: 0.85 }, "detected");
  sequence.addLabel("classification", ">");
  addDisappearingTargetClick(sequence, '[data-media-group="sku"]', "classification");
  sequence.to({}, { duration: 1.1 }, ">");
  sequence.addLabel("review", ">");
  addHighlightCue(sequence, "#mediaGrid", "review");
  sequence.to({}, { duration: 0.45 }, ">");
  sequence.addLabel("overview", ">");
  addDisappearingTargetClick(sequence, "#libraryBackButton", "overview");
  sequence.to({}, { duration: 0.75 }, ">");
  sequence.addLabel("download", ">");
  addHighlightedClick(sequence, "#saveButton", "download");
  hideCursor(sequence, ">+=0.12");
  sequence.to({}, { duration: 4.7 }, "download+=0.75");
  sequence.addLabel("complete", ">");
  sequence.addLabel("download-complete", ">+=0.42");
  sequence.to({}, { duration: 0.72 }, "download-complete");
  sequence.addLabel("archive-appear", ">");
  if (archivePanel.value && pluginView.value && stage.value) {
    sequence.to(pluginView.value, { y: -6, autoAlpha: 0, duration: 0.26, ease: "power2.out" }, "archive-appear");
    sequence.call(showArchiveView, [], ">");
    sequence.fromTo(archivePanel.value,
      { y: 10, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.36, ease: "power3.out", immediateRender: false },
      ">",
    );
  }
  sequence.addLabel("archive-open", ">+=0.35");
  addHighlightedClick(sequence, ".scripted-demo__archive-button", "archive-open");
  sequence.addLabel("folders-reveal", ">+=0.12");
  const folderRows = stage.value.querySelectorAll<HTMLElement>(".scripted-demo__archive-row");
  sequence.fromTo(folderRows,
    { y: 7, autoAlpha: 0 },
    { y: 0, autoAlpha: 1, duration: 0.28, stagger: 0.07, ease: "power2.out", immediateRender: false },
    "folders-reveal",
  );
  sequence.to({}, { duration: 3.5 }, ">");
  if (archivePanel.value) {
    sequence.to(archivePanel.value, { y: 4, autoAlpha: 0, duration: 0.22, ease: "power2.out" }, ">");
  }
  sequence.call(resetDemoState, [], ">");
  sequence.to({}, { duration: 0.18 }, ">");
  if (pluginView.value) {
    sequence.fromTo(pluginView.value,
      { autoAlpha: 0, y: 5 },
      { autoAlpha: 1, y: 0, duration: 0.24, ease: "power2.out", immediateRender: false },
      ">",
    );
  }
}

function scheduleCycleRestart(): void {
  cycleRestartCall?.kill();
  cycleRestartCall = gsap.delayedCall(0.4, () => {
    cycleRestartCall = null;
    const measured = measureFrameContent();
    if (measured) updateContentHeight(measured, false);
    if (!timeline || !frameReady.value || !heightReady.value || activeView.value !== "plugin" || !target("#detectionState")) return;
    timeline.pause(0).invalidate().play(0);
  });
}

function startDemo(): void {
  previewWindow()?.__TUCUI_PREVIEW_RESET__?.(cycleCount.value);
  void nextTick(() => {
    if (!frameReady.value || !heightReady.value || !target("#detectionState")) return;
    timeline?.pause(0).invalidate().play(0);
  });
}

function resetDemoState(): void {
  cycleCount.value += 1;
  pinStageHeight();
  currentTargetSelector = "";
  currentHaloSelector = "";
  if (cursor.value) gsap.set(cursor.value, { autoAlpha: 0, x: 348 * scale.value, y: 44 * scale.value });
  if (cursorIcon.value) gsap.set(cursorIcon.value, { scale: 1 });
  if (cursorRing.value) gsap.set(cursorRing.value, { autoAlpha: 0, scale: 0.22 });
  if (focusHalo.value) gsap.set(focusHalo.value, { autoAlpha: 0 });
  if (focusHalo.value) gsap.set(focusHalo.value, { clearProps: "transform,width,height,borderRadius" });
  archiveOpen.value = false;
  activeView.value = "plugin";
  if (archiveTree.value) gsap.set(archiveTree.value, { display: "none" });
  if (archivePanel.value) gsap.set(archivePanel.value, { display: "none", autoAlpha: 0, y: 0 });
  if (pluginView.value) gsap.set(pluginView.value, { display: "block", autoAlpha: 0, y: 5 });
  previewWindow()?.__TUCUI_PREVIEW_RESET__?.(cycleCount.value);
  cancelAnimationFrame(cycleMeasureFrameId);
  cycleMeasureFrameId = requestAnimationFrame(() => {
    observeFrameContent();
    const measured = measureFrameContent();
    if (measured) updateContentHeight(measured);
  });
}

function handleFrameLoad(): void {
  const measured = measureFrameContent();
  if (measured) updateContentHeight(measured, false);
  observeFrameContent();
  updateScale();
  readyDeadline = performance.now() + 6_000;
  pollFrameReady();
  readyTimeout?.kill();
  readyTimeout = gsap.delayedCall(6, () => {
    if (!frameReady.value) loadFailed.value = true;
  });
}

function retryFrame(): void {
  cancelAnimationFrame(readyFrameId);
  heightTween?.kill();
  heightTween = null;
  hasValidHeight = false;
  if (stage.value) gsap.set(stage.value, { clearProps: "height" });
  readyTimeout?.kill();
  loadFailed.value = false;
  heightReady.value = false;
  frameReady.value = false;
  contentHeight.value = 1;
  retryId.value += 1;
}

function handleVisibility(): void {
  if (document.hidden) timeline?.pause();
  else timeline?.resume();
}

onMounted(() => {
  if (!stage.value) return;
  gsapContext = gsap.context(() => {
    if (cursor.value) gsap.set(cursor.value, { autoAlpha: 0, transformOrigin: "3px 2px" });
  }, stage.value);
  media = gsap.matchMedia();
  media.add({
    desktop: "(min-width: 681px)",
    mobile: "(max-width: 680px)",
    reduceMotion: "(prefers-reduced-motion: reduce)",
  }, (context) => {
    const conditions = context.conditions as { desktop: boolean; mobile: boolean; reduceMotion: boolean };
    mobileDemo.value = conditions.mobile;
    reducedMotion.value = conditions.reduceMotion;
    if (conditions.reduceMotion) {
      showReducedMotionState();
    }
    else if (frameReady.value) {
      buildTimeline();
      void nextTick(startDemo);
    }
    return () => timeline?.kill();
  }, stage.value);
  resizeObserver = new ResizeObserver(updateScale);
  resizeObserver.observe(stage.value);
  intersectionObserver = new IntersectionObserver(([entry]) => {
    if (entry?.isIntersecting && !document.hidden) timeline?.resume();
    else timeline?.pause();
  }, { threshold: 0.2 });
  intersectionObserver.observe(stage.value);
  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("message", handlePreviewMessage);
  updateScale();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(readyFrameId);
  cancelAnimationFrame(cycleMeasureFrameId);
  timeline?.kill();
  cycleRestartCall?.kill();
  heightTween?.kill();
  readyTimeout?.kill();
  media?.revert();
  gsapContext?.revert();
  resizeObserver?.disconnect();
  frameResizeObserver?.disconnect();
  intersectionObserver?.disconnect();
  document.removeEventListener("visibilitychange", handleVisibility);
  window.removeEventListener("message", handlePreviewMessage);
});
</script>

<template>
  <section
    ref="stage"
    class="scripted-demo"
    :class="{ 'is-height-ready': heightReady && frameReady, 'has-load-error': loadFailed }"
    aria-label="图萃素材整理与下载流程演示"
    :data-demo-cycle="cycleCount"
    :data-accepted-height="contentHeight"
  >
    <p v-if="activeView === 'plugin'" class="visually-hidden">
      演示依次展示插件检测 30 个素材、打开 SKU 图分类、查看素材、返回总览、下载完成，并查看压缩包一级目录。下载包包含商品信息.txt、商品主图、商品视频、SKU图和详情图。
    </p>
    <p v-else class="visually-hidden">下载包包含商品信息.txt、商品主图 6 个、商品视频 1 个、SKU图 8 个和详情图 15 个。</p>
    <div ref="pluginView" class="scripted-demo__plugin" :aria-hidden="activeView !== 'plugin'" :inert="activeView !== 'plugin'">
      <div ref="viewport" class="scripted-demo__viewport" :style="viewportStyle">
        <div class="scripted-demo__canvas" :style="canvasStyle">
          <iframe
            ref="iframe"
            :key="iframeSource"
            :src="iframeSource"
            title="图萃 Extension 真实界面"
            tabindex="-1"
            aria-hidden="true"
            :style="iframeStyle"
            @load="handleFrameLoad"
          ></iframe>
        </div>
      </div>
    </div>
    <div ref="archivePanel" class="scripted-demo__archive" aria-label="压缩包内容预览" :aria-hidden="activeView !== 'archive'" :inert="activeView !== 'archive'">
      <button class="scripted-demo__archive-button" type="button" @click="revealArchiveTree">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3.5h9l3 3V20H6V3.5Zm9 0V7h3M9 10h6m-6 3h6m-6 3h4" /></svg>
        <span><strong>桌面磁吸理线器数据线固定夹….zip</strong><small>30 个素材及商品信息</small></span>
      </button>
      <div ref="archiveTree" class="scripted-demo__archive-tree">
        <div class="scripted-demo__archive-row is-file"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3.5h7l3 3V20H7V3.5Zm7 0V7h3" /></svg><span>商品信息.txt</span></div>
        <div class="scripted-demo__archive-row"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 7h6l1.5 2h9.5v10.5h-17V7Z" /></svg><span>商品主图</span><small>6</small></div>
        <div class="scripted-demo__archive-row"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 7h6l1.5 2h9.5v10.5h-17V7Z" /></svg><span>商品视频</span><small>1</small></div>
        <div class="scripted-demo__archive-row"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 7h6l1.5 2h9.5v10.5h-17V7Z" /></svg><span>SKU图</span><small>8</small></div>
        <div class="scripted-demo__archive-row"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 7h6l1.5 2h9.5v10.5h-17V7Z" /></svg><span>详情图</span><small>15</small></div>
      </div>
    </div>
    <span ref="cursor" class="scripted-demo__cursor" aria-hidden="true">
      <svg ref="cursorIcon" viewBox="0 0 28 32"><path d="M3.25 2.5 21.4 17.05l-8.1 1.1 4.65 8.15-3.35 1.9-4.7-8.2-5.45 6.2L3.25 2.5Z" /></svg>
      <span ref="cursorRing" class="scripted-demo__cursor-ring"></span>
    </span>
    <span ref="focusHalo" class="scripted-demo__halo" aria-hidden="true"></span>
    <div v-if="loadFailed" class="scripted-demo__error" role="status">
      <span>插件演示暂时没有加载完成。</span>
      <button type="button" @click="retryFrame">重新加载</button>
    </div>
  </section>
</template>
